import { httpsCallable, getFunctions } from 'firebase/functions';
import app, { db, isFirebaseReady } from '../config/firebase';

const functions = app ? getFunctions(app) : null;

export interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
  subject: string;
}

export interface ContactSubmissionResult {
  success: boolean;
  message: string;
  firestoreId?: string;
  emailSent?: boolean;
}

const getFriendlySubmitError = (error: any, fallback: string): string => {
  const code = String(error?.code || error?.status || error?.details?.status || '').toLowerCase();
  const message = String(error?.message || error?.details?.message || '');

  if (
    code.includes('resource-exhausted') ||
    code.includes('too-many-requests') ||
    message.toLowerCase().includes('too many requests')
  ) {
    return 'You are sending messages too quickly. Please wait a minute and try again.';
  }

  if (code.includes('invalid-argument')) {
    return 'Please check your form details and try again.';
  }

  return fallback;
};

/**
 * Submits the contact form through the `submitContactInquiry` Cloud Function,
 * which stores the inquiry in Firestore and sends the team notification email
 * via Brevo. All delivery happens server-side — the browser never talks to an
 * email provider.
 */
export const submitContactForm = async (
  formData: ContactFormData
): Promise<ContactSubmissionResult> => {
  try {
    if (!isFirebaseReady() || !db || !functions) {
      console.error('Contact form is unavailable: Firebase is not properly configured');
      return {
        success: false,
        message: 'Contact form is temporarily unavailable. Please contact us directly at info@bioaxis.co.za',
        emailSent: false
      };
    }

    const submitFn = httpsCallable(functions, 'submitContactInquiry');
    const response: any = await submitFn({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      company: formData.company,
      subject: formData.subject,
      message: formData.message
    });

    return {
      success: true,
      message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.',
      firestoreId: response?.data?.id,
      emailSent: !!response?.data?.emailSent
    };
  } catch (error) {
    console.error('Contact form submission failed:', error);
    return {
      success: false,
      message: getFriendlySubmitError(
        error,
        'Sorry, there was an issue submitting your message. Please try again or contact us directly at info@bioaxis.co.za'
      ),
      emailSent: false
    };
  }
};
